import { useState, useEffect } from 'react';
import { Download, X, Smartphone, Zap, WifiOff } from 'lucide-react';
import usePWA from '../hooks/usePWA';

interface InstallPromptProps {
  className?: string;
  /** Delay before showing the prompt (in ms) */
  delay?: number;
}

/**
 * Component that prompts users to install the PWA
 * Shows "Add to Home Screen" button when installable
 * Dismissible with localStorage persistence
 */
export function InstallPrompt({ className = '', delay = 3000 }: InstallPromptProps) {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedDate = dismissed ? new Date(dismissed) : null;

    // Show again after 7 days
    const shouldShow = !dismissedDate ||
      (Date.now() - dismissedDate.getTime()) > 7 * 24 * 60 * 60 * 1000;

    if (!shouldShow || isInstalled) {
      return;
    }

    // Delay showing the prompt
    if (isInstallable) {
      const timer = setTimeout(() => setShowPrompt(true), delay);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isInstalled, delay]);

  const handleInstallClick = async () => {
    setIsInstalling(true);

    try {
      const success = await installApp();
      if (success) {
        setShowPrompt(false);
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  if (!showPrompt || !isInstallable || isInstalled) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 mm-glass rounded-2xl shadow-2xl border border-white/10 z-40 ${className}`}
      role="dialog"
      aria-labelledby="install-prompt-title"
      aria-describedby="install-prompt-description"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-violet-500/15 rounded-2xl border border-violet-500/30 flex items-center justify-center">
            <Download className="w-5 h-5 text-violet-200" />
          </div>

          <div className="flex-1 min-w-0">
            <h3
              id="install-prompt-title"
              className="text-lg font-semibold text-white mb-1"
            >
              Install English Assessment
            </h3>
            <p
              id="install-prompt-description"
              className="text-sm text-white/70 mb-4"
            >
              Install our app for a better experience. Access it quickly from your home screen
              and use it offline.
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={handleInstallClick}
                disabled={isInstalling}
                className="mm-btn mm-btn-primary flex-1 sm:flex-none px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isInstalling ? 'Installing...' : 'Install App'}
              </button>
              <button
                onClick={handleDismiss}
                disabled={isInstalling}
                className="mm-btn mm-btn-secondary px-4 py-2 text-sm disabled:opacity-50"
              >
                Not Now
              </button>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            disabled={isInstalling}
            className="flex-shrink-0 text-white/50 hover:text-white transition-colors disabled:opacity-50"
            aria-label="Close install prompt"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact install button (e.g., for header or settings)
 */
export function InstallButton({ className = '' }: { className?: string }) {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [isInstalling, setIsInstalling] = useState(false);

  const handleInstallClick = async () => {
    setIsInstalling(true);
    try {
      await installApp();
    } catch (error) {
      console.error('Error showing install prompt:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  if (isInstalled || !isInstallable) {
    return null;
  }

  return (
    <button
      onClick={handleInstallClick}
      disabled={isInstalling}
      className={`inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      aria-label="Install application"
    >
      <Download className="w-4 h-4" />
      {isInstalling ? 'Installing...' : 'Install App'}
    </button>
  );
}

/**
 * Install benefits banner - shows features of installing the PWA
 */
export function InstallBanner({ className = '' }: { className?: string }) {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [isInstalling, setIsInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem('pwa-banner-dismissed');
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 14) {
        setDismissed(true);
      }
    }
  }, []);

  if (isInstalled || !isInstallable || dismissed) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      await installApp();
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', new Date().toISOString());
  };

  return (
    <div className={`bg-gradient-to-r from-blue-600 to-indigo-600 text-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <Smartphone className="w-6 h-6" />
            </div>
            <div>
              <p className="font-medium">Install MM English Assessment</p>
              <div className="flex items-center gap-4 text-sm text-white/80 mt-1">
                <span className="flex items-center gap-1">
                  <Zap className="w-4 h-4" /> Faster access
                </span>
                <span className="flex items-center gap-1">
                  <WifiOff className="w-4 h-4" /> Works offline
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50 transition-colors disabled:opacity-50"
            >
              {isInstalling ? 'Installing...' : 'Install'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Dismiss install banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
